import { Box, useTheme } from "@mui/material";
import {
  useLicensingServiceV1,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { Licensing_V1, UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useDeferredValue, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ConfirmationDialog,
  ExpirationDatePicker,
  SearchBar,
  SelectableList,
  SelectableListItem,
} from "../../../components";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import {
  useAllUserGroupsForACompany,
  USER_GROUPS_QUERY_KEY,
} from "../../../hooks/useAllUserGroupsForACompany";
import {
  LICENSE_ALLOCATIONS_QUERY_KEY,
  LICENSE_GROUP_ALLOCATIONS_QUERY_KEY,
} from "../../../hooks/useExternalExperiences";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { USER_GROUP_EXPERIENCES_QUERY_KEY } from "../../groups/hooks/useUserGroupExperiences";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import { GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../hooks/useExperienceGroups";
import { INCOMING_LICENSES_QUERY_KEY } from "../hooks/useIncomingLicenses";
import {
  USER_GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY,
  useUserGroupsForReleaseChannel,
} from "../hooks/useUserGroupsForReleaseChannel";

type GroupAccessData = UserManagement_V1.ReleaseChannel;
type TableAccessData = UserManagement_V1.UserGroupAccessData;

export interface GrantAccessToGroupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  releaseChannelId?: string;
  isExternal?: boolean;
  licenseId?: string;
  maxDate?: Date | null;
}

export const GrantAccessToGroupDialog: FC<GrantAccessToGroupDialogProps> = ({
  isOpen,
  onClose,
  releaseChannelId,
  isExternal = false,
  licenseId,
  maxDate,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [datePickerChecked, setDatePickerChecked] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const userManagementService = useUserManagementServiceV1();
  const licensingService = useLicensingServiceV1();
  const queryClient = useQueryClient();
  const { companyId } = useParams();
  const { groups: allGroups, isLoading } = useAllUserGroupsForACompany(companyId, true);

  const { groupAccess: existingGroupAccessIds, isLoading: isLoadingAccess } =
    useUserGroupsForReleaseChannel(releaseChannelId, {
      enabled: isOpen,
      isExternal,
    });

  const mappedItems = useMemo(() => {
    return allGroups.map<SelectableListItem>((group) => {
      const hasAccess = existingGroupAccessIds.some(
        (access) => access.userGroupId === group.userGroupId,
      );

      return {
        id: group.userGroupId,
        primaryText: group.userGroupName,
        secondaryText: t("experiences.grantAccessToGroup.usersCount", {
          count: group.userCount,
        }),
        tertiaryText: hasAccess
          ? t("experiences.grantAccessToGroup.hasAccess")
          : undefined,
        type: hasAccess ? "hasAccess" : "selectable",
      };
    });
  }, [allGroups, existingGroupAccessIds, t]);

  const filteredItems = useMemo(() => {
    if (deferredSearchTerm.trim()) {
      return mappedItems.filter((item) =>
        item.primaryText
          .toLowerCase()
          .includes(deferredSearchTerm.toLowerCase()),
      );
    }

    return mappedItems;
  }, [deferredSearchTerm, mappedItems]);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
  };

  const handleSearchClear = () => {
    setSearchTerm("");
  };

  const handleGroupSelect = useCallback((groupIdToSelect: string) => {
    setSelectedGroupId(groupIdToSelect);
  }, []);

  const handleRemoveGroup = useCallback(() => {
    setSelectedGroupId(null);
  }, []);

  const handleSubmit = async () => {
    if (selectedGroupId && (expirationDate || datePickerChecked)) {
      setIsSubmitLoading(true);
      const mutation = isExternal ? externalMutation : addGroupAccessMutation;
      await mutation.mutateAsync({
        groupId: selectedGroupId,
        expiresAt: datePickerChecked
          ? undefined
          : (expirationDate ?? undefined),
      });
    }
    setIsSubmitLoading(false);
    handleClose();
  };

  const handleClose = () => {
    onClose();
    setSelectedGroupId(null);
    setSearchTerm("");
    setExpirationDate(null);
    setDatePickerChecked(true);
  };

  // --- Helper for Optimistic Updates ---
  const updateCacheOptimistically = (groupId: string, expiresAt?: Date) => {
    const dialogKeys = [
      [USER_GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    ];

    dialogKeys.forEach((key) => {
      queryClient.setQueryData<GroupAccessData[]>(key, (oldData) => {
        if (!oldData) return oldData;
        const newEntry = new UserManagement_V1.ReleaseChannel({
          releaseChannelId: releaseChannelId!,
          userGroupId: groupId,
          requirements: UserManagement_V1.ReleaseChannelRequirements.Optional,
          expiresAt: expiresAt ?? undefined,
        });

        return [...oldData, newEntry];
      });
    });

    const tableKeys = [
      [GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    ];

    tableKeys.forEach((key) => {
      queryClient.setQueryData<TableAccessData[]>(key, (oldData) => {
        const currentData = oldData ?? [];

        const newTableEntry = new UserManagement_V1.UserGroupAccessData({
          userGroupId: groupId,
          expiresAt: expiresAt ?? undefined,
        });

        return [...currentData, newTableEntry];
      });
    });
  };

  const invalidateGroupList = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [USER_GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    });

    void queryClient.invalidateQueries({
      queryKey: [GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    });
  }, [queryClient, releaseChannelId]);

  const addGroupAccessMutation = useMutation({
    mutationFn: ({
      groupId,
      expiresAt,
    }: {
      groupId: string;
      expiresAt?: Date;
    }) => {
      const releaseChannel = new UserManagement_V1.ReleaseChannel({
        releaseChannelId: releaseChannelId!,
        requirements: UserManagement_V1.ReleaseChannelRequirements.Optional,
        userGroupId: groupId,
        expiresAt,
      });

      return userManagementService.addReleaseChannelToUserGroup(
        groupId,
        releaseChannel,
      );
    },
    onSuccess: (_, { groupId, expiresAt }) => {
      // OPTIMISTIC UPDATE: Update UI immediately
      updateCacheOptimistically(groupId, expiresAt);

      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_EXPERIENCES_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_QUERY_KEY],
      });

      // Double Invalidation for consistency
      invalidateGroupList();
      setTimeout(invalidateGroupList, 1000);
    },
  });

  const externalMutation = useMutation({
    mutationFn: ({
      groupId,
      expiresAt,
    }: {
      groupId: string;
      expiresAt?: Date;
    }) => {
      const allocation = new Licensing_V1.LicenseGroupAllocationWithoutId({
        userGroupId: groupId,
        expiresAt: expiresAt ?? undefined,
        isPinnedToLicenseExpiration: !expiresAt,
        isRevoked: false,
      });

      return licensingService.allocateLicenseGroupSeat(licenseId!, allocation);
    },
    onSuccess: (_, { groupId, expiresAt }) => {
      // OPTIMISTIC UPDATE: Update UI immediately
      updateCacheOptimistically(groupId, expiresAt);

      void queryClient.invalidateQueries({
        queryKey: [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_ALLOCATIONS_QUERY_KEY, licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_QUERY_KEY],
      });

      // Double Invalidation for consistency
      invalidateGroupList();
      setTimeout(invalidateGroupList, 1000);
    },
  });

  const actionText = useMemo(() => {
    if (isSubmitLoading) {
      return t("experiences.grantAccessToGroup.granting");
    }
    if (isMobile) {
      return t("experiences.grantAccessToGroup.grant");
    }

    return t("experiences.grantAccessToGroup.grantAccess");
  }, [isMobile, isSubmitLoading, t]);

  const selectedItems = useMemo(() => {
    return mappedItems.filter((item) => item.id === selectedGroupId);
  }, [mappedItems, selectedGroupId]);

  return (
    <ConfirmationDialog.Root
      aria-describedby="grant-access-to-group-description"
      aria-labelledby="grant-access-to-group-title"
      fullScreenOnMobile={true}
      fullWidth
      height="728px"
      maxWidth="xs"
      onClose={handleClose}
      open={isOpen}
    >
      <ConfirmationDialog.Title
        id="grant-access-to-group-title"
        showIcon={false}
        sx={{ fontSize: { xs: 18, md: 24 } }}
      >
        {t("experiences.grantAccessToGroup.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        sx={{
          fontWeight: 500,
          color: theme.colors.text[1],
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
          <SearchBar
            handleClear={handleSearchClear}
            handleSubmit={handleSearchSubmit}
            onChange={handleSearchChange}
            placeholder={t("experiences.grantAccessToGroup.searchPlaceholder")}
            sx={{ maxWidth: undefined }}
            value={searchTerm}
          />
        </form>
        <SelectableList
          emptyMessage={t("experiences.grantAccessToGroup.noGroupsAvailable")}
          height="100%"
          isLoading={isLoading || isLoadingAccess}
          items={filteredItems}
          maxHeight="100%"
          minHeight={0}
          multiSelect={false}
          onChipRemove={handleRemoveGroup}
          onItemSelect={handleGroupSelect}
          selectedId={selectedGroupId}
          selectedItems={selectedItems}
          selectedItemsTitle={t(
            "experiences.grantAccessToGroup.selectedGroups",
          )}
          sx={{ flex: 1, minHeight: 0 }}
        />
        <Box sx={{ visibility: selectedGroupId ? "visible" : "hidden" }}>
          <ExpirationDatePicker
            checked={datePickerChecked}
            disabled={false}
            isExternal={isExternal}
            key={selectedGroupId ? "active" : "inactive"}
            maxDate={maxDate ?? undefined}
            onChange={setExpirationDate}
            setChecked={setDatePickerChecked}
            value={expirationDate}
          />
        </Box>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleClose}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("experiences.grantAccessToGroup.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={
            !selectedGroupId || (!datePickerChecked && expirationDate === null)
          }
          isLoading={isSubmitLoading}
          loadingText={t("experiences.grantAccessToGroup.granting")}
          onClick={() => void handleSubmit()}
          variant="contained"
        >
          {actionText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
